import PropTypes from 'prop-types';
import * as R from 'ramda';
import React from 'react';
import Table from 'react-bootstrap/Table';
import { Link } from 'react-router-dom';

import { PATH, SORT_DIRECTIONS } from '../../constants';
import { formatDBDate } from '../../utils/format-date';

const { ASC, DESC } = SORT_DIRECTIONS;

const SortIcon = ({ isActive, dir }) => {
    let icon = '↕';

    if (isActive) {
        if (dir === ASC) {
            icon = '↑';
        } else {
            icon = '↓';
        }
    }

    return (
        <span className={`sort-icon${isActive ? ' is-active' : ''}`} aria-hidden="true">
            {icon}
        </span>
    );
};

// The per-lab action links. Extracted so the row is not `td > ul > li > Link`
// deep inline with five conditional branches.
const LabActions = ({ lab, appAccess, tableContext }) => (
    <ul>
        <li>
            <Link to={PATH.labAction(lab.id, PATH.lab_actions.VIEW)}>View</Link>
        </li>
        {appAccess.write ? (
            <li>
                <Link to={PATH.labAction(lab.id, PATH.lab_actions.EDIT)}>Edit</Link>
            </li>
        ) : null}
        <li>
            <Link to={PATH.labAction(lab.id, PATH.lab_actions.INSTRUCTIONS)}>Instructions</Link>
        </li>
        {appAccess.write && tableContext === 'Event' ? (
            <li>
                <Link to={PATH.labAction(lab.id, PATH.lab_actions.LABSTART)}>Lab 2 Start Date</Link>
            </li>
        ) : null}
        {appAccess.write && lab.type === 'VPN' ? (
            <li>
                <Link to={PATH.labAction(lab.id, PATH.lab_actions.CERTS)}>Upload cert/key</Link>
            </li>
        ) : null}
    </ul>
);

const LabRow = ({ lab, appAccess, tableContext }) => (
    <tr>
        <td>{lab.id}</td>
        <td>{lab.name}</td>
        <td>{lab.status}</td>
        <td>{lab.type}</td>
        <td>
            {formatDBDate(lab.live_event_start)} - {formatDBDate(lab.live_event_end)}
        </td>
        <td>
            {lab.on_demand_training_id_start} - {lab.on_demand_training_id_end}
        </td>
        <td>
            <LabActions lab={lab} appAccess={appAccess} tableContext={tableContext} />
        </td>
    </tr>
);

const LabTable = ({ appAccess, labs, changeOrderBy, orderBy, dir, tableContext }) => (
    <Table striped bordered hover>
        <thead>
            <tr>
                <th {...(changeOrderBy ? { onClick: () => changeOrderBy('id'), className: 'sortable-field' } : {})}>
                    ID{changeOrderBy && <SortIcon isActive={orderBy === 'id'} dir={dir} />}
                </th>
                <th {...(changeOrderBy ? { onClick: () => changeOrderBy('name'), className: 'sortable-field' } : {})}>
                    Name{changeOrderBy && <SortIcon isActive={orderBy === 'name'} dir={dir} />}
                </th>
                <th {...(changeOrderBy ? { onClick: () => changeOrderBy('status'), className: 'sortable-field' } : {})}>
                    Status{changeOrderBy && <SortIcon isActive={orderBy === 'status'} dir={dir} />}
                </th>
                <th>Type</th>
                <th>Live Event (Start-End)</th>
                <th>OnDemand ID (Start-End)</th>
                <th>Actions</th>
            </tr>
        </thead>
        <tbody>
            {R.map(
                (lab) => (
                    <LabRow key={lab.id} lab={lab} appAccess={appAccess} tableContext={tableContext} />
                ),
                labs,
            )}
        </tbody>
    </Table>
);

LabActions.propTypes = {
    lab: PropTypes.object,
    appAccess: PropTypes.object,
    tableContext: PropTypes.string,
};

LabRow.propTypes = {
    lab: PropTypes.object,
    appAccess: PropTypes.object,
    tableContext: PropTypes.string,
};

LabTable.propTypes = {
    appAccess: PropTypes.object,
    labs: PropTypes.arrayOf(
        PropTypes.shape({
            id: PropTypes.number,
            live_event_start: PropTypes.string,
            live_event_end: PropTypes.string,
            name: PropTypes.string,
            status: PropTypes.string,
            type: PropTypes.string,
        }),
    ),
    changeOrderBy: PropTypes.func,
    orderBy: PropTypes.string,
    dir: PropTypes.oneOf([ASC, DESC]),
    tableContext: PropTypes.string,
};

SortIcon.propTypes = {
    isActive: PropTypes.bool,
    dir: PropTypes.oneOf([ASC, DESC]),
};

export default LabTable;
